import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';
import Logo from './Logo';
import './Navbar.css';

const Navbar: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState<boolean>(false);
  const [showMobileMenu, setShowMobileMenu] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [cartItemCount, setCartItemCount] = useState<number>(0);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Extract search query from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || '';
    setSearchTerm(q);
  }, [location.search]);

  // Fetch cart item count
  useEffect(() => {
    const fetchCartCount = async (): Promise<void> => {
      try {
        const response = await api.get<Array<{ id: number; quantity: number }>>('/cart');
        const totalItems = response.data.reduce((sum, item) => sum + item.quantity, 0);
        setCartItemCount(totalItems);
      } catch (error) {
        // Silently fail - cart count is non-critical
        setCartItemCount(0);
      }
    };
    fetchCartCount();
    // Refresh cart count when navigating (in case items were added/removed)
    const interval = setInterval(fetchCartCount, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [location.pathname]);

  const handleLogout = (): void => {
    logout();
    navigate('/');
    setShowUserMenu(false);
  };

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showUserMenu]);

  return (
    <nav className="navbar" aria-label="Main navigation">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <div className="navbar-container">
        <Link to="/" className="navbar-brand">
          <Logo className="phi-logo-img" />
          <span className="brand-name">PHI PRINTING</span>
        </Link>
        
        {/* Desktop Menu */}
        <div className="navbar-menu desktop-menu">
          <div className="nav-links">
            <Link 
              to="/" 
              className={`navbar-link ${location.pathname === '/' ? 'active' : ''}`}
              aria-current={location.pathname === '/' ? 'page' : undefined}
            >
              Home
            </Link>
            <Link 
              to="/art" 
              className={`navbar-link ${location.pathname.startsWith('/art') ? 'active' : ''}`}
              aria-current={location.pathname.startsWith('/art') ? 'page' : undefined}
            >
              Gallery
            </Link>
            <Link 
              to="/create-print" 
              className={`navbar-link ${location.pathname === '/create-print' ? 'active' : ''}`}
              aria-current={location.pathname === '/create-print' ? 'page' : undefined}
            >
              Create Print
            </Link>
            <Link 
              to="/become-an-artist" 
              className={`navbar-link ${location.pathname === '/become-an-artist' ? 'active' : ''}`}
              aria-current={location.pathname === '/become-an-artist' ? 'page' : undefined}
            >
              Become an Artist
            </Link>
          </div>
        </div>

        <form 
          className="navbar-search desktop-menu"
          onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            const query = (formData.get('search') as string)?.trim();
            if (query) {
              navigate(`/art/browse?q=${encodeURIComponent(query)}`);
            } else {
              navigate('/art');
            }
          }}
        >
          <input
            type="text"
            name="search"
            placeholder="Search artworks, artists..."
            aria-label="Search artworks and artists"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="navbar-search-input"
          />
          <button type="submit" className="navbar-search-button" aria-label="Search">
            <i className="fas fa-search" aria-hidden="true"></i>
          </button>
        </form>
          
        <div className="navbar-actions desktop-menu">
            <Link
              to="/cart"
              className="cart-icon-link"
              aria-label={cartItemCount > 0 ? `Shopping cart, ${cartItemCount} items` : 'Shopping cart'}
            >
              <i className="fas fa-shopping-cart" aria-hidden="true"></i>
              <span className="cart-badge">
                {cartItemCount > 0 ? `Cart (${cartItemCount})` : 'Cart'}
              </span>
            </Link>
            
            <button 
              onClick={toggleTheme} 
              className="theme-toggle"
              aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              <i className={theme === 'light' ? 'fas fa-moon' : 'fas fa-sun'} aria-hidden="true"></i>
            </button>

            {isAuthenticated ? (
              <div className="user-menu" ref={userMenuRef}>
                <button
                  className="user-menu-button"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  aria-expanded={showUserMenu}
                  aria-haspopup="menu"
                >
                  <i className="fas fa-user" aria-hidden="true"></i>
                  <span>{user?.displayName || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email}</span>
                  <i className={`fas fa-chevron-${showUserMenu ? 'up' : 'down'}`} aria-hidden="true"></i>
                </button>
                
                {showUserMenu && (
                  <div className="user-menu-dropdown">
                    <Link to="/history" className="user-menu-item" onClick={() => setShowUserMenu(false)}>
                      <i className="fas fa-history"></i> Purchase History
                    </Link>
                    <Link to="/profile" className="user-menu-item" onClick={() => setShowUserMenu(false)}>
                      <i className="fas fa-user-circle"></i> Profile
                    </Link>
                    {user?.role === 'Artist' && (
                      <Link to="/artist/metrics" className="user-menu-item" onClick={() => setShowUserMenu(false)}>
                        <i className="fas fa-chart-line"></i> Artist Metrics
                      </Link>
                    )}
                    <Link to="/addresses" className="user-menu-item" onClick={() => setShowUserMenu(false)}>
                      <i className="fas fa-address-book"></i> Addresses
                    </Link>
                    <Link to="/returns" className="user-menu-item" onClick={() => setShowUserMenu(false)}>
                      <i className="fas fa-undo"></i> Returns
                    </Link>
                    {user?.role === 'Admin' && (
                      <Link to="/admin" className="user-menu-item" onClick={() => setShowUserMenu(false)}>
                        <i className="fas fa-cog"></i> Admin Dashboard
                      </Link>
                    )}
                    <button className="user-menu-item user-menu-logout" onClick={handleLogout}>
                      <i className="fas fa-sign-out-alt"></i> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="btn btn-primary btn-small">
                Login
              </Link>
            )}
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="mobile-menu-toggle"
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          aria-label={showMobileMenu ? 'Close menu' : 'Open menu'}
          aria-expanded={showMobileMenu}
          aria-controls="mobile-menu"
        >
          <i className={`fas fa-${showMobileMenu ? 'times' : 'bars'}`} aria-hidden="true"></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="mobile-menu" id="mobile-menu">
          <Link to="/" className="mobile-link" onClick={() => setShowMobileMenu(false)}>Home</Link>
          <Link to="/art" className="mobile-link" onClick={() => setShowMobileMenu(false)}>Gallery</Link>
          <Link to="/create-print" className="mobile-link" onClick={() => setShowMobileMenu(false)}>Create Print</Link>
          <Link to="/become-an-artist" className="mobile-link" onClick={() => setShowMobileMenu(false)}>Become an Artist</Link>
          <Link to="/cart" className="mobile-link" onClick={() => setShowMobileMenu(false)}>Cart</Link>
          {isAuthenticated ? (
            <>
              <Link to="/history" className="mobile-link" onClick={() => setShowMobileMenu(false)}>Purchase History</Link>
              <Link to="/profile" className="mobile-link" onClick={() => setShowMobileMenu(false)}>Profile</Link>
              {user?.role === 'Artist' && (
                <Link to="/artist/metrics" className="mobile-link" onClick={() => setShowMobileMenu(false)}>Artist Metrics</Link>
              )}
              <Link to="/addresses" className="mobile-link" onClick={() => setShowMobileMenu(false)}>Addresses</Link>
              <Link to="/returns" className="mobile-link" onClick={() => setShowMobileMenu(false)}>Returns</Link>
              {user?.role === 'Admin' && (
                <Link to="/admin" className="mobile-link" onClick={() => setShowMobileMenu(false)}>Admin Dashboard</Link>
              )}
              <button className="mobile-link mobile-logout" onClick={handleLogout}>Logout</button>
            </>
          ) : (
            <Link to="/login" className="mobile-link" onClick={() => setShowMobileMenu(false)}>Login</Link>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;

